import classNames from 'classnames';
import React from 'react';

import {GwgAccelerator} from '@udemy/career-academies';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {IconButton, Image, Link} from '@udemy/react-core-components';
import {ModalTrigger} from '@udemy/react-dialog-components';
import {ExerciseInfoModal} from '@udemy/react-exercise-info-modal';
import {ContentArea} from '@udemy/react-layout-components';
import {Ribbon} from '@udemy/react-messaging-components';
import {toLocaleDateString} from '@udemy/shared-utils';
import {PlanPeriodStyle} from '@udemy/subscription-browse';
import {useActiveSubscriptions} from '@udemy/subscription-graphql';

import {GrowWithGoogleCTA} from 'src/components/grow-with-google-cta/grow-with-google-cta.react-component';
import {GwgPriceDisplay} from 'src/components/gwg-price-display';
import {ApiInstructor} from 'src/features/google-ai-path-page/types/course';
import {isSubscriberToAnyGwgPlan} from 'src/features/google-ai-plan-page/utils/is-gwg-subscriber';

import {DemoVideoPlayer} from '../demo-video-player/';
import {HeroCertificateCard} from './hero-certificate-card.react-component';
import {HeroProgressCard} from './hero-progress-card.react-component';
import styles from './paths-lp-hero.module.less';

import type {CertificateShareLinks} from 'src/components/grow-with-google/google-tracker/components/google-redirect-buttons.react-component';

export type PathHeroTheme = 'teal' | 'purple' | 'red' | 'orange';

const PATH_HERO_SLUG_TO_THEME: Record<string, PathHeroTheme> = {
    'ai-professional-certificate-learning-path': 'purple',
    'prompting-essentials-learning-path': 'teal',
    'accelerate-job-search-with-ai-learning-path': 'red',
    'ai-essentials-learning-path': 'orange',
};

function getPathHeroThemeFromSlug(slug: string | undefined): PathHeroTheme | undefined {
    if (!slug) return undefined;
    return PATH_HERO_SLUG_TO_THEME[slug];
}

interface CertificateInfo {
    certificateImageUrl: string;
}

export interface PathsLpHeroProps {
    title: string;
    description: string;
    accelerator: GwgAccelerator;
    instructor?: ApiInstructor;
    courseId?: number;
    courseTrackingId?: string;
    certificateInfo?: CertificateInfo | null;
    backgroundTheme?: PathHeroTheme;
    // Enrollment and progress props
    isEnrolled?: boolean;
    totalCourses?: number;
    completedCourses?: number;
    certificate?: CertificateShareLinks;
    nextCourseUrl?: string;
    nextCourseNumber?: number;
    /** Progress of the next course (0-100). Undefined when all courses completed. */
    nextCourseProgress?: number;
}

const PathsLpHero = (props: PathsLpHeroProps) => {
    const {
        title,
        description,
        accelerator,
        instructor,
        courseId,
        courseTrackingId,
        certificateInfo,
        backgroundTheme: backgroundThemeProp,
        isEnrolled = false,
        totalCourses = 0,
        completedCourses = 0,
        certificate,
        nextCourseUrl,
        nextCourseNumber,
        nextCourseProgress,
    } = props;
    const {gettext} = useI18n();
    const currentlySubscribedPlan = useActiveSubscriptions()[0];

    const acceleratorSlug = accelerator.slug;
    const theme: PathHeroTheme =
        backgroundThemeProp ?? getPathHeroThemeFromSlug(acceleratorSlug) ?? 'purple';

    // Use instructor data if available
    const creatorName = instructor?.name;
    const creatorUrl = instructor?.url;

    const courseSeriesCount = accelerator.curriculum.sections.length ?? undefined;

    const stats = accelerator.stats;
    const avgRating = stats?.averageRating ?? undefined;
    const durationInSeconds = stats?.durationInSeconds ?? 0;
    const totalContentHours = Math.round((durationInSeconds / 3600) * 10) / 10;
    const numberOfPracticeExercises = stats?.numberOfPracticeExercises ?? undefined;

    // Enrolled user states
    const isCompleted = completedCourses === totalCourses && totalCourses > 0;

    // Format certificate completion date
    const formattedCompletionDate = certificate?.completionDate
        ? toLocaleDateString(new Date(certificate.completionDate), {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
          })
        : undefined;

    return (
        <div
            className={classNames(styles['hero-background'], styles[`hero-background--${theme}`])}
            data-testid="hero-background"
        >
            <section className={classNames('ud-full-viewport-container')}>
                <div className={styles['hero-wrapper']} data-testid="hero-wrapper">
                    <div className={styles['hero-svg-overlay']} aria-hidden="true" />
                    <div
                        className={classNames(styles['hero-container'], 'ud-container')}
                        data-testid="hero-container"
                    >
                        <div className={styles['hero-box']}>
                            <ContentArea className={styles['area']}>
                                <div className={styles['content-section']}>
                                    {/* Google Logo */}
                                    <Image
                                        src="https://frontends.udemycdn.com/frontends-marketplace-experience/staticx/udemy/images/grow-with-google/google-logo.svg"
                                        alt="Google"
                                        width={100}
                                        height={32}
                                    />

                                    {/* Title */}
                                    <h1 className={classNames(styles['hero-title'])}>{title}</h1>

                                    {/* Badges - TODO: Make these dynamic/configurable */}
                                    {!isEnrolled && (
                                        <div className={styles['badges-container']}>
                                            <Ribbon color="purple-150" className={styles['badge']}>
                                                <Icon
                                                    glyph="sparkles"
                                                    size="xsmall"
                                                    label={false}
                                                />
                                                {gettext('Learn AI with Google')}
                                            </Ribbon>
                                            {certificateInfo !== null && (
                                                <Ribbon
                                                    color="teal-150"
                                                    className={styles['badge']}
                                                >
                                                    <Icon
                                                        glyph="certificate"
                                                        size="xsmall"
                                                        label={false}
                                                    />
                                                    {gettext('Certificate issued on completion')}
                                                </Ribbon>
                                            )}
                                        </div>
                                    )}

                                    {/* Description */}
                                    <p className={classNames(styles['description'])}>
                                        {description}
                                    </p>

                                    {/* Metrics */}
                                    {!isEnrolled && (
                                        <div className={styles['metrics-container']}>
                                            {!!avgRating && (
                                                <div className={styles['metric']}>
                                                    <div className={styles['metric-header']}>
                                                        <Icon
                                                            glyph="rating-star"
                                                            size="medium"
                                                            label="Rating"
                                                            className={classNames(
                                                                styles['icon'],
                                                                styles['highlight'],
                                                            )}
                                                        />
                                                        <span className={styles['metric-value']}>
                                                            {avgRating.toLocaleString(undefined, {
                                                                maximumFractionDigits: 1,
                                                            })}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            styles['metric-label'],
                                                        )}
                                                    >
                                                        {gettext('average rating')}
                                                    </div>
                                                </div>
                                            )}
                                            {!!courseSeriesCount && (
                                                <div className={styles['metric']}>
                                                    <div className={styles['metric-header']}>
                                                        <span className={styles['metric-value']}>
                                                            {courseSeriesCount}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            styles['metric-label'],
                                                        )}
                                                    >
                                                        {gettext('course series')}
                                                    </div>
                                                </div>
                                            )}
                                            {!!numberOfPracticeExercises && (
                                                <div className={styles['metric']}>
                                                    <div className={styles['metric-header']}>
                                                        <span className={styles['metric-value']}>
                                                            {numberOfPracticeExercises}+
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            styles['metric-label'],
                                                        )}
                                                    >
                                                        {gettext('practice activities')}{' '}
                                                        <ModalTrigger
                                                            trigger={
                                                                <IconButton
                                                                    className={
                                                                        styles['exercise-info-icon']
                                                                    }
                                                                    size="xsmall"
                                                                    udStyle="ghost"
                                                                    aria-label={gettext(
                                                                        'Toggles hands-on practice information modal',
                                                                    )}
                                                                >
                                                                    <Icon
                                                                        label={false}
                                                                        glyph="info-outline"
                                                                        size="small"
                                                                        color="subdued"
                                                                    />
                                                                </IconButton>
                                                            }
                                                            renderModal={(props) => (
                                                                <ExerciseInfoModal {...props} />
                                                            )}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                            {!!totalContentHours && (
                                                <div className={styles['metric']}>
                                                    <div className={styles['metric-header']}>
                                                        <span className={styles['metric-value']}>
                                                            {totalContentHours}
                                                        </span>
                                                    </div>
                                                    <div
                                                        className={classNames(
                                                            styles['metric-label'],
                                                        )}
                                                    >
                                                        {gettext('hours of content')}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* Creator Attribution */}
                                    {creatorName && (
                                        <div className={styles['creator-attribution']}>
                                            <Icon glyph="google" size="xsmall" label={false} />
                                            <span>
                                                {gettext('Created by')}{' '}
                                                {creatorUrl ? (
                                                    <Link
                                                        to={creatorUrl}
                                                        className={'ud-link-underline'}
                                                        disableRouter={creatorUrl === '#'}
                                                    >
                                                        {creatorName}
                                                    </Link>
                                                ) : (
                                                    creatorName
                                                )}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className={styles['video-section']}>
                                    {!isEnrolled ? (
                                        <>
                                            {/* Not enrolled: Show video preview */}
                                            <div className={styles['video-container']}>
                                                <DemoVideoPlayer
                                                    acceleratorSlug={acceleratorSlug}
                                                    thumbnailUrl={
                                                        accelerator.careerHeroImages?.humanImage
                                                    }
                                                />
                                            </div>

                                            {/* CTA Section - GWG Subscription CTA */}
                                            <div className={styles['cta-container']}>
                                                <GrowWithGoogleCTA
                                                    acceleratorId={accelerator.numericId}
                                                    acceleratorTitle={title}
                                                    courseId={courseId}
                                                    courseTrackingId={courseTrackingId}
                                                    uiRegion="google-ai-path-hero"
                                                    offerCtaStyle="primary"
                                                    size="large"
                                                    className={styles['cta-button']}
                                                    planPeriodStyle={PlanPeriodStyle.SINGLE}
                                                    includeTrialDaysOnCta={false}
                                                    showSubtitle={false}
                                                    subtitleClassName={styles['billing-text']}
                                                />
                                                {!isSubscriberToAnyGwgPlan(
                                                    currentlySubscribedPlan,
                                                ) && (
                                                    <div className={styles['cta-subtext']}>
                                                        {gettext('Carlos ')}
                                                        <br
                                                            className={styles['cta-subtext-break']}
                                                        />
                                                        <GwgPriceDisplay />{' '}
                                                        <Link
                                                            to="/partner/google/learn-ai/"
                                                            className={classNames(
                                                                'ud-link-underline',
                                                            )}
                                                            disableRouter={true}
                                                        >
                                                            {gettext('Learn more')}
                                                        </Link>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {isCompleted ? (
                                                <HeroCertificateCard
                                                    title={title}
                                                    completedDate={formattedCompletionDate}
                                                    certificateUrl={certificate?.certificateUrl}
                                                />
                                            ) : (
                                                // In Progress: Show progress ring and CTA
                                                <HeroProgressCard
                                                    totalCourses={totalCourses}
                                                    completedCourses={completedCourses}
                                                    nextCourseUrl={nextCourseUrl}
                                                    nextCourseNumber={nextCourseNumber}
                                                    nextCourseProgress={nextCourseProgress}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                            </ContentArea>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export {PathsLpHero};
